import React, { useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { authAPI } from '../../services/api'
import Button from '../../components/ui/Button'
import Card from '../../components/ui/Card'

const ResetPasswordPage = () => {
  const { t } = useTranslation('auth')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    watch,
  } = useForm()

  const password = watch('password')

  const onSubmit = async (data) => {
    setIsLoading(true)
    try {
      await authAPI.resetPassword({ token, password: data.password })
      setIsSuccess(true)
      setTimeout(() => navigate('/login', { state: { passwordReset: true } }), 3000)
    } catch (err) {
      const message = err.response?.data?.message || t('resetPassword.errorFallback')
      setError('root', { message })
    } finally {
      setIsLoading(false)
    }
  }

  // Brak tokenu w URL
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ background: 'var(--color-bg)' }}>
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1f9f93] p-2" style={{ boxShadow: '0 10px 22px rgba(31,159,147,.22)' }}>
                <img src="/logo.png" alt="Finomi" className="w-10 h-10 object-contain brightness-0 invert" />
              </div>
            </div>
            <h2 className="mt-6 text-[28px] font-black tracking-[.2px]" style={{ color: 'var(--color-text)' }}>
              {t('resetPassword.invalidLink')}
            </h2>
          </div>
          <Card>
            <div className="text-center py-4">
              <p className="text-sm font-semibold mb-4" style={{ color: 'var(--color-text-muted)' }}>
                {t('resetPassword.invalidLinkMessage')}
              </p>
              <Link
                to="/forgot-password"
                className="text-sm font-bold no-underline"
                style={{ color: '#1f9f93' }}
              >
                {t('resetPassword.sendNewLink')}
              </Link>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ background: 'var(--color-bg)' }}>
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1f9f93] p-2" style={{ boxShadow: '0 10px 22px rgba(31,159,147,.22)' }}>
              <img src="/logo.png" alt="Finomi" className="w-10 h-10 object-contain brightness-0 invert" />
            </div>
          </div>
          <h2 className="mt-6 text-[28px] font-black tracking-[.2px]" style={{ color: 'var(--color-text)' }}>
            {t('resetPassword.title')}
          </h2>
          <p className="mt-2 text-[13px] font-semibold" style={{ color: 'var(--color-text-muted)' }}>
            {t('resetPassword.subtitle')}
          </p>
        </div>

        <Card>
          {isSuccess ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(31,159,147,.12)' }}>
                <svg className="w-6 h-6" style={{ color: '#1f9f93' }} fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--color-text)' }}>
                {t('resetPassword.successTitle')}
              </h3>
              <p className="text-sm font-semibold mb-4" style={{ color: 'var(--color-text-muted)' }}>
                {t('resetPassword.successMessage')}
              </p>
              <Link
                to="/login"
                className="text-sm font-bold no-underline"
                style={{ color: '#1f9f93' }}
              >
                {t('resetPassword.goToLogin')}
              </Link>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              {/* Nowe haslo */}
              <div>
                <label htmlFor="password" className="form-label">
                  {t('resetPassword.newPassword')}
                </label>
                <div className="relative mt-1">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="form-input pr-10"
                    placeholder={t('resetPassword.newPasswordPlaceholder')}
                    {...register('password', {
                      required: t('validation:passwordRequired'),
                      minLength: {
                        value: 8,
                        message: t('validation:passwordMinLength')
                      },
                      pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                        message: t('validation:passwordPattern')
                      }
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" style={{ color: 'var(--color-text-faint)' }} />
                    ) : (
                      <EyeIcon className="h-5 w-5" style={{ color: 'var(--color-text-faint)' }} />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="form-error mt-1">{errors.password.message}</p>
                )}
              </div>

              {/* Potwierdzenie hasla */}
              <div>
                <label htmlFor="confirmPassword" className="form-label">
                  {t('resetPassword.confirmPassword')}
                </label>
                <div className="relative mt-1">
                  <input
                    id="confirmPassword"
                    type={showConfirm ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="form-input pr-10"
                    placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                    {...register('confirmPassword', {
                      required: t('validation:confirmPasswordRequired'),
                      validate: (value) =>
                        value === password || t('validation:passwordsMismatch')
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirm(!showConfirm)}
                  >
                    {showConfirm ? (
                      <EyeSlashIcon className="h-5 w-5" style={{ color: 'var(--color-text-faint)' }} />
                    ) : (
                      <EyeIcon className="h-5 w-5" style={{ color: 'var(--color-text-faint)' }} />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="form-error mt-1">{errors.confirmPassword.message}</p>
                )}
              </div>

              {errors.root && (
                <div className="rounded-xl p-3" style={{ background: 'rgba(229,83,83,.10)', border: '1px solid rgba(229,83,83,.18)' }}>
                  <p className="text-sm font-bold" style={{ color: '#e55353' }}>{errors.root.message}</p>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={isLoading}
                className="w-full"
              >
                {t('resetPassword.submit')}
              </Button>
            </form>
          )}
        </Card>

        {!isSuccess && (
          <div className="text-center">
            <p className="text-sm font-semibold" style={{ color: 'var(--color-text-muted)' }}>
              {t('resetPassword.rememberPassword')}{' '}
              <Link
                to="/login"
                className="font-bold no-underline"
                style={{ color: '#1f9f93' }}
              >
                {t('resetPassword.loginLink')}
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ResetPasswordPage
